import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { hasThumbPicture, MinimumSlideInformation } from '@pcp/core/interfaces';
import { isChapterSlide, isLectionSlide, isNotNullOrUndefined } from '@pcp/core/util';
import { distinctUntilChanged, map } from 'rxjs';
import { IsVideoSlidePipe } from 'shared/classroom/preview-screen/ui-input-pipes';
import { LineBreaksPipe } from 'shared/ui/directives';
import { PcpSwipeEvent, SwiperDirective } from 'shared/ui/swiper';

/**
 * Basic slides row of the beamer nav: [‹] [swiper] [›] — three cells of the layout's grid (`display: contents`).
 * Pure UI: the layout handles the outputs.
 * Styles: libs/styles/slide-sync/beamer-navigation/_nav-parts.scss (global, beamer themes).
 */
@Component({
    selector: 'pcp-nav-basic-slides-row',
    templateUrl: './nav-basic-slides-row.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIconButton, MatIcon, SwiperDirective, LineBreaksPipe, IsVideoSlidePipe, TranslocoPipe],
})
export class NavBasicSlidesRowComponent {
    readonly slides = input<MinimumSlideInformation[]>([]);
    /** Basic slide (0…n-1) shown in the preview. */
    readonly slideIdx = input(0);
    /** Basic slide that is active on the beamer. */
    readonly currentSlideIdx = input(0);
    /** The basic slides preview is open. */
    readonly visible = input(false);
    /** Thumbs per row of the extension preview; class `length{n}` keeps the same width (no layout shift). */
    readonly visibleCount = input.required<number>();

    readonly slideClicked = output<MinimumSlideInformation>();
    readonly previousClicked = output<void>();
    readonly nextClicked = output<void>();
    /** The user swiped to another basic slide. */
    readonly swiped = output<number>();

    /** Follows the input, but a swipe moves it at once (before the store answers). */
    protected readonly shownIdx = linkedSignal(() => this.slideIdx());
    /** Fires each time the basic preview is shown or hidden (the swiper re-measures). */
    protected readonly updateSwiper$ = toObservable(this.visible).pipe(
        distinctUntilChanged(),
        map(() => void 0),
    );
    protected readonly isPrevious = computed(() => this.shownIdx() === this.currentSlideIdx() - 1);
    protected readonly isNext = computed(() => this.shownIdx() === this.currentSlideIdx() + 1);

    protected swipe(event: PcpSwipeEvent) {
        this.shownIdx.set(event.swipeIndex);
        this.swiped.emit(event.swipeIndex);
    }

    protected readonly showCaption = (slide: MinimumSlideInformation) =>
        isNotNullOrUndefined(slide) && slide.showCaption;
    protected readonly isChapterOrLectionWithoutPreview = (slide: MinimumSlideInformation) =>
        isNotNullOrUndefined(slide) &&
        (isChapterSlide(slide) || isLectionSlide(slide)) &&
        !hasThumbPicture(slide);
}
